import { FADE_MASK_STYLE } from "@/utils/masonryMask"

// Solarized theme colors
const _colors = {
  // Dark mode (Solarized Dark)
  dark: {
    keyword: "#268bd2", // blue - import, def, if, for, elif, in
    function: "#b58900", // yellow - function names, methods
    string: "#2aa198", // cyan - string literals
    number: "#d33682", // magenta - numbers
    variable: "#93a1a1", // base1 - variables, default text
    punctuation: "#839496", // base0 - operators, brackets
    comment: "#586e75", // base01 - comments
  },
  // Light mode (Solarized Light)
  light: {
    keyword: "#268bd2", // blue
    function: "#b58900", // yellow
    string: "#2aa198", // cyan
    number: "#d33682", // magenta
    variable: "#657b83", // base00
    punctuation: "#586e75", // base01
    comment: "#93a1a1", // base1
  },
}

interface CodeLineProps {
  lineNumber: number
  children: React.ReactNode
}

function CodeLine({ lineNumber, children }: CodeLineProps) {
  return (
    <div className="flex">
      <span className="w-8 text-right pr-6 text-fg-secondary select-none shrink-0 opacity-60">
        {lineNumber}
      </span>
      <span className="flex-1">{children}</span>
    </div>
  )
}

// Syntax highlighting components
function Keyword({ children }: { children: string }) {
  return (
    <span className="text-[#268bd2]" style={{ fontWeight: 700 }}>
      {children}
    </span>
  )
}

function Func({ children }: { children: string }) {
  return <span className="text-[#b58900]">{children}</span>
}

function Str({ children }: { children: string }) {
  return <span className="text-[#2aa198]">{children}</span>
}

function Num({ children }: { children: number | string }) {
  return <span className="text-[#d33682]">{children}</span>
}

function Var({ children }: { children: string }) {
  return <span className="text-fg-default">{children}</span>
}

function Op({ children }: { children: string }) {
  return <span className="text-fg-secondary">{children}</span>
}

function Indent({ level }: { level: number }) {
  return <span>{" ".repeat(level * 4)}</span>
}

function PythonCodeViewer() {
  return (
    <div
      className="relative border-t border-l border-solid border-border-default rounded-tl-xl bg-bg-default"
      style={{
        height: "320px",
        overflow: "hidden",
        ...FADE_MASK_STYLE,
      }}
    >
      {/* Content area */}
      <div className="p-6">
        {/* Python Code Viewer */}
        <div className="font-mono text-sm leading-relaxed whitespace-pre">
          <CodeLine lineNumber={1}>
            <Keyword>import</Keyword> <Var>os</Var>
          </CodeLine>
          <CodeLine lineNumber={2}>
            <Keyword>import</Keyword> <Var>requests</Var>
          </CodeLine>
          <CodeLine lineNumber={3}>{""}</CodeLine>
          <CodeLine lineNumber={4}>
            <Keyword>def</Keyword> <Func>get_custom_ratio</Func>
            <Op>(</Op>
            <Var>project_id</Var>
            <Op>):</Op>
          </CodeLine>
          <CodeLine lineNumber={5}>
            <Indent level={1} />
            <Var>url</Var> <Op>=</Op> <Keyword>f</Keyword>
            <Str>{'"https://api.tokenterminal.com/v2/projects/'}</Str>
            <Op>{"{"}</Op>
            <Var>project_id</Var>
            <Op>{"}"}</Op>
            <Str>{'/metrics"'}</Str>
          </CodeLine>
          <CodeLine lineNumber={6}>{""}</CodeLine>
          <CodeLine lineNumber={7}>
            <Indent level={1} />
            <Var>headers</Var> <Op>=</Op> <Op>{"{"}</Op>
          </CodeLine>
          <CodeLine lineNumber={8}>
            <Indent level={2} />
            <Str>{'"accept"'}</Str>
            <Op>:</Op> <Str>{'"application/json"'}</Str>
            <Op>,</Op>
          </CodeLine>
          <CodeLine lineNumber={9}>
            <Indent level={2} />
            <Str>{'"authorization"'}</Str>
            <Op>:</Op> <Keyword>f</Keyword>
            <Str>{'"Bearer '}</Str>
            <Op>{"{"}</Op>
            <Var>os</Var>
            <Op>.</Op>
            <Var>environ</Var>
            <Op>.</Op>
            <Func>get</Func>
            <Op>(</Op>
            <Str>{"'TOKEN_TERMINAL_API_KEY'"}</Str>
            <Op>)</Op>
            <Op>{"}"}</Op>
            <Str>{'"'}</Str>
          </CodeLine>
          <CodeLine lineNumber={10}>
            <Indent level={1} />
            <Op>{"}"}</Op>
          </CodeLine>
          <CodeLine lineNumber={11}>{""}</CodeLine>
          <CodeLine lineNumber={12}>
            <Indent level={1} />
            <Var>response</Var> <Op>=</Op> <Var>requests</Var>
            <Op>.</Op>
            <Func>get</Func>
            <Op>(</Op>
            <Var>url</Var>
            <Op>,</Op> <Var>headers</Var>
            <Op>=</Op>
            <Var>headers</Var>
            <Op>)</Op>
          </CodeLine>
          <CodeLine lineNumber={13}>{""}</CodeLine>
          <CodeLine lineNumber={14}>
            <Indent level={1} />
            <Keyword>if</Keyword> <Var>response</Var>
            <Op>.</Op>
            <Var>status_code</Var> <Op>==</Op> <Num>200</Num>
            <Op>:</Op>
          </CodeLine>
          <CodeLine lineNumber={15}>
            <Indent level={2} />
            <Var>data</Var> <Op>=</Op> <Var>response</Var>
            <Op>.</Op>
            <Func>json</Func>
            <Op>()</Op>
          </CodeLine>
          <CodeLine lineNumber={16}>{""}</CodeLine>
          <CodeLine lineNumber={17}>
            <Indent level={2} />
            <Keyword>for</Keyword> <Var>metric</Var> <Keyword>in</Keyword> <Var>data</Var>
            <Op>:</Op>
          </CodeLine>
          <CodeLine lineNumber={18}>
            <Indent level={3} />
            <Keyword>if</Keyword> <Var>metric</Var>
            <Op>[</Op>
            <Str>{"'metric'"}</Str>
            <Op>]</Op> <Op>==</Op> <Str>{"'daus'"}</Str>
            <Op>:</Op>
          </CodeLine>
          <CodeLine lineNumber={19}>
            <Indent level={4} />
            <Var>daus</Var> <Op>=</Op> <Var>metric</Var>
            <Op>[</Op>
            <Str>{"'value'"}</Str>
            <Op>]</Op>
          </CodeLine>
          <CodeLine lineNumber={20}>
            <Indent level={3} />
            <Keyword>elif</Keyword> <Var>metric</Var>
            <Op>[</Op>
            <Str>{"'metric'"}</Str>
            <Op>]</Op> <Op>==</Op> <Str>{"'fees'"}</Str>
            <Op>:</Op>
          </CodeLine>
        </div>
      </div>
    </div>
  )
}

export { PythonCodeViewer }
